import { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { SR_ALLOCATIONS_STAGE, DEVICE_ALL, srStagesFor } from './constants';
import { helpText, Help } from './help';
import { formatShortDate, formatMetric, displayDeviceLabel } from './format';
import { sdkVersionMatches, runMetrics, deviceOptions, isSrStageMetric, srStageValue } from './metrics';
import { LegendToggle, useHiddenSet } from './charts';

// One device's runs, oldest first, one line per stage that device reported.
function buildSrTrendData(runs, stages) {
  const datasets = stages
    .map((stage) => ({
      label: stage.label,
      data: runs.map((run) => srStageValue(run.metrics, stage.metric)),
      borderColor: stage.color,
      backgroundColor: stage.color,
      tension: 0.25,
      pointRadius: 4,
      unit: stage.unit || 'ms',
      ...(stage.yAxisID ? { yAxisID: stage.yAxisID } : {}),
    }))
    .filter((dataset) => dataset.data.some((value) => value !== null));
  return { labels: runs.map((run) => formatShortDate(run.uploadedAt)), datasets };
}

export function SessionReplayTrend({ history, metrics, selectedPlatform, selectedDevice, selectedSdkVersion, loading }) {
  const stages = [...srStagesFor(selectedPlatform), ...(selectedPlatform === 'android' ? [SR_ALLOCATIONS_STAGE] : [])];
  const srMetrics = metrics.filter((metric) => (
    metric.platform === selectedPlatform
    && isSrStageMetric(metric)
    && stages.some((stage) => stage.metric === metric.name)
    && sdkVersionMatches(metric, selectedSdkVersion)
    && history?.some((run) => run.id === metric.runId && run[selectedPlatform]?.available)
  ));
  const allDevices = selectedDevice === DEVICE_ALL;
  const devices = allDevices ? deviceOptions(srMetrics, selectedPlatform) : [selectedDevice];
  const [pickedDevice, setPickedDevice] = useState(null);
  const activeDevice = devices.includes(pickedDevice) ? pickedDevice : devices[0] || null;

  // Runs the device on show reported Session Replay metrics in, oldest first.
  const trendRunEntries = activeDevice ? (history || [])
    .filter((run) => run[selectedPlatform]?.available)
    .slice()
    .reverse()
    .map((run) => ({ ...run, metrics: runMetrics(srMetrics, run.id, selectedPlatform, activeDevice, selectedSdkVersion) }))
    .filter(({ metrics: runMetricsForRun }) => runMetricsForRun.length) : [];
  const data = buildSrTrendData(trendRunEntries, stages);
  const [hiddenStages, toggleStage] = useHiddenSet();
  const shownData = { ...data, datasets: data.datasets.map((dataset) => ({ ...dataset, hidden: hiddenStages.has(dataset.label) })) };
  const hasData = data.datasets.length > 0;
  const shownStages = stages.filter((stage) => data.datasets.some((dataset) => dataset.label === stage.label));
  const hasAllocations = shownStages.includes(SR_ALLOCATIONS_STAGE);

  return (
    <div className="sdkp-panel sdkp-section-spaced" data-panel="session-replay">
      <h2 className="sdkp-panel-title">Session Replay performance trend</h2>
      <p className="sdkp-panel-hint">
        Average time each Session Replay capture stage
        <Help text={helpText('stage', selectedPlatform)} />
        {' takes per run'}
        {activeDevice ? ` on ${displayDeviceLabel(activeDevice)}` : ''}
        , one color per stage.
        {hasAllocations && (
          <>
            {' Main thread allocations'}
            <Help text={helpText('alloc', selectedPlatform)} />
            {' use the right-hand count axis.'}
          </>
        )}
      </p>
      {allDevices && devices.length > 0 && (
        <div className="sdkp-segmented sdkp-device-picker" aria-label="Session Replay device">
          {devices.map((device) => (
            <button
              key={device}
              type="button"
              className={device === activeDevice ? 'sdkp-active' : ''}
              aria-pressed={device === activeDevice}
              onClick={() => setPickedDevice(device)}
            >
              {displayDeviceLabel(device)}
            </button>
          ))}
        </div>
      )}
      {loading && <div className="sdkp-empty">Loading…</div>}
      {!loading && !hasData && <div className="sdkp-empty">No Session Replay trend data available for the selected device and SDK version.</div>}
      {!loading && hasData && (
        <>
          <div className="sdkp-chart">
            <Line
              data={shownData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.dataset.label}: ${formatMetric(context.parsed.y, context.dataset.unit)}`,
                    },
                  },
                },
                scales: {
                  x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
                  y: {
                    beginAtZero: true,
                    position: 'left',
                    title: { display: true, text: 'Duration (ms)', font: { size: 11, weight: 'bold' } },
                  },
                  allocations: {
                    type: 'linear',
                    display: 'auto',
                    position: 'right',
                    beginAtZero: true,
                    grid: { drawOnChartArea: false },
                    title: { display: true, text: 'Main thread allocations (count)', font: { size: 11, weight: 'bold' } },
                  },
                },
              }}
            />
          </div>
          <div className="sdkp-legend sdkp-legend-center">
            {shownStages.map((stage) => (
              <span key={stage.metric}>
                <LegendToggle hidden={hiddenStages.has(stage.label)} onToggle={() => toggleStage(stage.label)} swatchStyle={{ background: stage.color }}>
                  {stage.label}
                </LegendToggle>
                <Help text={helpText(stage.help, selectedPlatform)} />
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
